import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor, releaseIdempotencyKey } from '@/lib/http-core';
import { errorFromResponse } from '@/lib/errors';

/*
 * 상품 텍스트 옵션(고도몰 es_goodsOptionText → catalog product_options) — 구매 시 고객이 옵션 줄마다 입력하는 값
 * (예: "국적 Nationality"). 주문당 한 번 받는 "추가 입력 · 동의"(productCollection)와는 다른 기능이다.
 *
 * catalog AdminProductOptionController (generated 타입 미반영 → adminFetch):
 *   GET    /admin/v1/products/{productId}/options              MD_MANAGER·SUPER_ADMIN·AUDITOR
 *   POST   /admin/v1/products/{productId}/options              MD_MANAGER·SUPER_ADMIN (201)
 *   PUT    /admin/v1/products/{productId}/options/{optionId}   MD_MANAGER·SUPER_ADMIN
 *   DELETE /admin/v1/products/{productId}/options/{optionId}   MD_MANAGER·SUPER_ADMIN (204)
 */

export type TextOptionType = 'TEXT' | 'TEXTAREA' | 'NUMBER' | 'SELECT';
export const TEXT_OPTION_TYPES: ReadonlyArray<TextOptionType> = ['TEXT', 'TEXTAREA', 'NUMBER', 'SELECT'];

export interface TextOptionChoice {
  choiceId?: string | null;
  label: string;
  additionalAmount: number | null;
  position: number;
}

export interface TextOption {
  optionId: string;
  name: string;
  optionType: TextOptionType;
  required: boolean;
  maxLength: number | null;
  position: number;
  choices: ReadonlyArray<TextOptionChoice>;
}

/** ProductOptionRequest — SELECT 가 아니면 choices 는 BE 가 무시, maxLength 는 텍스트류에서만 의미. */
export interface TextOptionRequest {
  name: string;
  optionType: TextOptionType;
  required: boolean;
  maxLength: number | null;
  position: number;
  choices: ReadonlyArray<TextOptionChoice>;
}

export const TEXT_OPTION_WRITE_ROLES = ['MD_MANAGER', 'SUPER_ADMIN'] as const;

export const productTextOptionsQueryKey = (productId: string) =>
  ['admin', 'product-text-options', productId] as const;

const basePath = (productId: string) => `/admin/v1/products/${encodeURIComponent(productId)}/options`;

interface Envelope<T> {
  success?: boolean;
  data?: T;
}

const byPosition = (a: { position: number }, b: { position: number }) => a.position - b.position;

const normalize = (o: Partial<TextOption>): TextOption => ({
  optionId: o.optionId ?? '',
  name: o.name ?? '',
  optionType: (o.optionType ?? 'TEXT') as TextOptionType,
  required: Boolean(o.required),
  maxLength: o.maxLength ?? null,
  position: o.position ?? 0,
  choices: [...(o.choices ?? [])].sort(byPosition),
});

export const useProductTextOptionsQuery = (productId: string | null | undefined) =>
  useQuery<ReadonlyArray<TextOption>>({
    queryKey: productTextOptionsQueryKey(productId ?? ''),
    enabled: Boolean(productId),
    queryFn: async () => {
      const res = await adminFetch(basePath(productId as string));
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as Envelope<Partial<TextOption>[]>;
      return (env.data ?? []).map(normalize).sort(byPosition);
    },
  });

/** 요청 본문 정리 — 유형에 맞지 않는 값은 비운다(NUMBER·SELECT 는 글자 수 없음, SELECT 만 선택지). */
export const toTextOptionRequest = (r: TextOptionRequest): TextOptionRequest => ({
  name: r.name.trim(),
  optionType: r.optionType,
  required: r.required,
  maxLength: r.optionType === 'TEXT' || r.optionType === 'TEXTAREA' ? r.maxLength : null,
  position: r.position,
  choices:
    r.optionType === 'SELECT'
      ? r.choices.map((c, i) => ({ ...c, label: c.label.trim(), position: c.position ?? i }))
      : [],
});

const send = async (method: 'POST' | 'PUT' | 'DELETE', path: string, body?: TextOptionRequest) => {
  const res = await adminFetch(path, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      'Idempotency-Key': idempotencyKeyFor(method, path, body ?? null),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) throw await errorFromResponse(res);
  // 같은 본문 쓰기의 키는 페이지 수명 동안 재사용된다 — 성공 후 풀어 줘야 "추가→삭제→같은 값 다시 추가"가
  // 첫 응답 재생으로 묻히지 않는다.
  releaseIdempotencyKey(method, path, body ?? null);
};

export const useProductTextOptionMutations = (productId: string) => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: productTextOptionsQueryKey(productId) });

  const create = useMutation({
    mutationFn: (body: TextOptionRequest) => send('POST', basePath(productId), toTextOptionRequest(body)),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ optionId, body }: { optionId: string; body: TextOptionRequest }) =>
      send('PUT', `${basePath(productId)}/${encodeURIComponent(optionId)}`, toTextOptionRequest(body)),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (optionId: string) => send('DELETE', `${basePath(productId)}/${encodeURIComponent(optionId)}`),
    onSuccess: invalidate,
  });
  return { create, update, remove };
};
